import { Dispatch, KeyboardEvent, SetStateAction, useEffect, useState } from 'react';
import { css, cx } from '@linaria/atomic';
import { Icon } from '@frontend/icons';
import { fontSize, theme } from '@frontend/theme';
import { BaseFieldProps, NakedButton, Text, TextLink, linariaStyles } from '@frontend/design-system';

const fontSizeStylesAliasStyle1 = css`
  font-size: var(--font-size-styles-alias-inline-expr0);
`;

const editableTextDivStyle8 = css`
  padding-bottom: ${theme.spacing(1)};
  display: flex;
`;

const editableTextDivStyle9 = css`
  border-bottom: 1px solid transparent;
  &:hover,
  &:focus-within {
    border-bottom: 1px solid var(--editable-text-div-inline-expr0);
  }
`;

const editableTextDivStyle10 = css`
  border-bottom: 1px solid var(--editable-text-div-inline-expr0);
`;

const editableTextInputStyle11 = css`
  height: var(--editable-text-input-inline-expr0);
`;

const editableTextTextLinkStyle12 = css`
  padding: ${theme.spacing(0, 2)};
`;

const editableTextNakedButtonStyle13 = css`
  &:hover,
  &:focus-within {
    .edit-icon {
      opacity: 1;
    }
  }
`;

const editableTextIconVisibleStyle = css`
  opacity: 1;
`;

const editableTextIconHiddenStyle = css`
  opacity: 0;
`;

interface EditableTextProps {
  isTitle?: boolean;
  fieldProps: BaseFieldProps;
  helperText?: string;
  disabled?: boolean;
  isToggledOpen?: boolean;
  setIsToggleOpen?: Dispatch<SetStateAction<boolean>>;
  hideEditIcon?: boolean;
  actionText?: string;
  onActionClick?: () => void;
  alwaysShowIcon?: boolean;
  hideUntilHovered?: boolean;
  trackingId?: string;
  size?: 'small' | 'medium' | 'large';
}

export const EditableText = ({
  isTitle = false,
  fieldProps,
  helperText,
  disabled = false,
  isToggledOpen,
  setIsToggleOpen,
  hideEditIcon,
  actionText,
  onActionClick,
  alwaysShowIcon = false,
  hideUntilHovered = false,
  trackingId,
  size,
  ...rest
}: EditableTextProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [prevActive, setPrevActive] = useState(false);

  const getFontSize = () => {
    if (size === 'small') return 16;
    if (size === 'medium') return 24;
    if (size === 'large') return 36;
    return isTitle ? 36 : 24;
  };
  const fontSizeNumber = getFontSize();
  const buttonId = 'editable-text-action-button';

  const getBorderColor = () => {
    if (prevActive && !!fieldProps.error && fieldProps.required) {
      return theme.colors.critical50;
    } else if (isEditing && fieldProps.active) {
      return theme.colors.primary50;
    } else {
      if (isTitle && !isEditing) {
        return 'transparent';
      }
      return theme.colors.neutral10;
    }
  };

  const setFocus = () => {
    if (disabled) return;
    setIsEditing(true);
    setPrevActive(true);
    setTimeout(() => {
      const focusableElement = document.getElementById(fieldProps.id);
      if (focusableElement) focusableElement.focus();
    }, 0);
  };

  /*
    This useEffect is to handle if we are triggering the input in edit mode outside the component
    if no function is passed to handle the state update, line 66 with return or else it will go into an infinite loop
    when you try to toggle it
  */

  useEffect(() => {
    if (!setIsToggleOpen) return;
    if (isToggledOpen && !isEditing) {
      setIsEditing(true);
      setFocus();
    } else {
      setIsEditing(false);
      setIsToggleOpen(false);
    }
  }, [isToggledOpen]);

  const { active, touched, ...restFieldProps } = fieldProps;

  return (
    <>
      <div
        role='button'
        onClick={setFocus}
        {...rest}
        style={
          {
            '--editable-text-div-inline-expr0': getBorderColor(),
          } as React.CSSProperties
        }
        className={cx(editableTextDivStyle8, hideUntilHovered ? editableTextDivStyle9 : editableTextDivStyle10)}
      >
        {isEditing && !disabled ? (
          <>
            <input
              {...restFieldProps}
              data-trackingid={trackingId ? `${trackingId}-input` : undefined}
              className={cx(fontSizeStylesAliasStyle1, editInputStyles, editableTextInputStyle11)}
              style={
                {
                  '--font-size-styles-alias-inline-expr0': fontSize(fontSizeNumber),
                  '--editable-text-input-inline-expr0': `${fontSizeNumber * 1.5}px`,
                } as React.CSSProperties
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter') setIsEditing(false);
              }}
              onBlur={(e) => {
                restFieldProps.onBlur(e);
                if (e.relatedTarget?.id === buttonId) return;
                setIsEditing(false);
                setIsToggleOpen?.(false);
              }}
            />
            {actionText && (
              <TextLink
                id={buttonId}
                weight='bold'
                className={editableTextTextLinkStyle12}
                onClick={(e) => {
                  e.stopPropagation();
                  onActionClick?.();
                }}
                trackingId={trackingId ? `${trackingId}-action-button` : undefined}
              >
                {actionText}
              </TextLink>
            )}
          </>
        ) : (
          <NakedButton
            trackingId={trackingId ? `${trackingId}-btn` : undefined}
            disabled={disabled}
            className={cx(readBtnStyles, editableTextNakedButtonStyle13)}
            onKeyDown={(e: KeyboardEvent<HTMLButtonElement>) => {
              if (e.key === 'Enter') {
                setFocus();
              }
            }}
          >
            <Text
              as='span'
              color={disabled ? 'disabled' : 'default'}
              weight='bold'
              className={cx(
                'editable-text-span',
                fontSizeStylesAliasStyle1,
                readTextStyles,
                fieldProps.value === '' && placeholderColorStyles,
                linariaStyles.truncate
              )}
              style={
                {
                  '--font-size-styles-alias-inline-expr0': fontSize(fontSizeNumber),
                } as React.CSSProperties
              }
            >
              {fieldProps.value || fieldProps.placeholder}
            </Text>
            {!hideEditIcon && (
              <Icon
                name='edit-small'
                className={cx(
                  'edit-icon',
                  editIconStyles,
                  alwaysShowIcon || fieldProps?.value?.length === 0 || disabled
                    ? editableTextIconVisibleStyle
                    : editableTextIconHiddenStyle
                )}
              />
            )}
          </NakedButton>
        )}
      </div>
      {!isTitle && helperText && (
        <Text
          size='small'
          className={cx(helperTextStylesClass, !!fieldProps.error && prevActive && helperTextErrorStyle)}
        >
          {helperText}
        </Text>
      )}
    </>
  );
};

const placeholderColor = theme.colors.neutral50;

const editInputStyles = css`
  background-color: transparent;
  font-weight: 700;
  border: none;
  outline: none;
  width: 100%;
  padding-left: ${theme.spacing(1)};
  &::placeholder {
    color: ${placeholderColor};
  }
`;

const readTextStyles = css`
  text-align: left;
  padding-left: ${theme.spacing(1)};
  margin: 0;
`;

const placeholderColorStyles = css`
  color: ${placeholderColor};
`;

const readBtnStyles = css`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  &:disabled {
    cursor: unset;
  }
`;

const editIconStyles = css`
  margin-left: ${theme.spacing(1)};
  margin-right: ${theme.spacing(1)};
`;

const helperTextStylesClass = css`
  color: ${theme.colors.neutral50};
  margin-top: ${theme.spacing(1)};
`;

const helperTextErrorStyle = css`
  color: ${theme.colors.critical50};
`;
